/* eslint-disable @next/next/no-img-element */
import { Body1, ButtonText, Headline1 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import useMedia from 'lib/hooks/useMedia'
import PlusPlanLogo from 'assets/pro-plan/plus_plan_logo.webp'
import NewButton from 'components/elements/NewButton'
import PaymentsMethods from 'assets/pro-plan/payment_method.webp'
import _useTranslation from 'lib/hooks/_useTranslation'
import { parseLocale } from 'lib/constants/localization'
import { onPageEnter } from 'lib/tracking/pageEnter'
import { useRouter } from 'next/router'
import { useState, useEffect } from 'react'
import Cloud from 'assets/pro-plan/cloud.webp'
import KnowCloud from 'assets/pro-plan/know_cloud.png'
import { KnowunityNumbers } from 'lib/constants/KnowunityNumbers'
import { getKnowToDownload } from 'lib/tracking/storage'
import { Know } from 'interfaces/Know'
import BadgePDF from 'assets/pro-plan/badge_pdf.png'
import BadgePrint from 'assets/pro-plan/badge_print.png'
import BadgeDownload from 'assets/pro-plan/badge_download.png'
import PlusPlanPriceLabel from './PlusPlanPriceLabel'

const Container = styled.div`
    display: grid;
    min-height: 80vh;

    grid-template-columns: 1fr 1fr;
    grid-gap: 40px;
    padding: 0 5%;

    @media (max-height: 800px) and (min-width: 1051px) {
        min-height: 800px;
    }
    position: relative;
    .image {
        display: flex;
        justify-content: center;
        overflow: hidden;
        position: absolute;
        bottom: 0;
        right: 0;
        width: 657px;
        height: 640px;

        .pdf {
            position: absolute;
            bottom: 125px;
            z-index: 21;
        }
        .download {
            position: absolute;
            top: 275px;
            z-index: 21;
            left: 85px;
        }
        .print {
            position: absolute;
            top: 95px;
            right: 95px;
            z-index: 21;
        }

        .benefits {
            position: absolute;
            bottom: 97.5px;
            display: grid;
            gap: 57.5px;
            width: 350px;
            left: 135px;
            p {
                font-size: 18px;
                font-family: var(--font-inter);
                color: ${theme.colors.white};
            }
        }
    }

    .mobile-image {
        display: none;
    }
    @media (max-width: 1050px) {
        overflow: hidden;
        grid-gap: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        flex-direction: column;
        padding: 25px;
        max-width: 100vw;

        img {
            position: relative;
            margin-top: 50px;
        }
    }
    @media (max-width: 750px) {
        height: fit-content;
        min-height: 60vh;
    }
`

const TextWrapper = styled.div<{ isDarkTheme?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: start;
    flex-direction: column;
    padding-top: 25px;
    overflow: hidden;
    gap: 20px;
    * {
        z-index: 20px;
    }
    button {
        display: flex;
        justify-content: start;
        @media (max-width: 1050px) {
            width: 100%;
        }
    }
    .subtitle {
        width: 80%;
    }
    & h1 {
        font-weight: 700;

        @media (max-width: 1050px) {
            font-size: 30px;
            padding: 0 5%;
            line-height: 35px;
            text-align: center;
        }
        @media (max-width: 750px) {
            text-align: center;
        }
    }
    @media (max-width: 750px) {
        padding-top: 0;
    }
`

const KnowThumbnail = styled.div<{ pageNumber: 1 | 2 | 3 }>`
    position: absolute;
    left: 10px;
    top: 100px;
    width: fit-content;
    border-radius: ${theme.borderRadius.normal};
    transform: rotate(-20deg);
    img {
        object-fit: fill;
        border: 7.5px solid #6899c399;
        border-radius: ${theme.borderRadius.huge};
        ${({ pageNumber }) => css`
            position: relative;
            left: ${pageNumber * 90}px;
            top: ${pageNumber * 40}px;
            transform: rotate(${pageNumber * 10}deg);
        `}
    }
`

interface Props {
    onGetPlusPlanButtonClick: (value: string) => void
}

const VariantHeader = ({ onGetPlusPlanButtonClick }: Props) => {
    const [storedKnowToDownload, setStoredKnowToDownload] = useState<Know>()
    const md = useMedia(deviceSize.lg)
    const router = useRouter()
    const { countryCode } = parseLocale(router.locale)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('plusPlan')

    useEffect(() => {
        const knowToDownload = getKnowToDownload()

        if (!knowToDownload) return

        setStoredKnowToDownload(knowToDownload)
    }, [])

    useEffect(() => {
        onPageEnter()
    }, [tCommon, countryCode, t])

    const isKnowToDownloadStored = !!storedKnowToDownload

    const heroSource = isKnowToDownloadStored ? KnowCloud : Cloud

    const imageSource =
        storedKnowToDownload?.knowDocumentPages?.length && storedKnowToDownload?.knowDocumentPages.length > 1
            ? storedKnowToDownload.knowDocumentPages
                  .map((doc) => doc.imageUrl)
                  .splice(0, 3)
                  .reverse()
            : [null, storedKnowToDownload?.thumbnailMediumUrl]

    return (
        <Container>
            <TextWrapper>
                <img src={PlusPlanLogo.src} height={PlusPlanLogo.height} width={PlusPlanLogo.width} alt="Plus plan logo" />
                <Headline1 color={theme.colors.white}>
                    {t(
                        isKnowToDownloadStored
                            ? 'plusPlan/downloadGetBetterGradesHeadline'
                            : 'plusPlan/subscriptionBetterGradesHeadline'
                    )}
                </Headline1>
                <PlusPlanPriceLabel />
                <NewButton
                    borderRadius={theme.borderRadius.normal}
                    color={theme.colors.white}
                    width={md ? undefined : 450}
                    fullWidth={md}
                    onClick={() => onGetPlusPlanButtonClick('hero')}
                >
                    <ButtonText color={theme.colors.businessBlue}>
                        {t('plusPlan/checkoutSectionSubscribeNowHeadline')}
                    </ButtonText>
                </NewButton>
                {!md ? (
                    // eslint-disable-next-line jsx-a11y/alt-text
                    <img src={PaymentsMethods.src} width={PaymentsMethods.width} height={PaymentsMethods.height} />
                ) : null}
            </TextWrapper>
            {!md ? (
                <div className="image">
                    {isKnowToDownloadStored ? (
                        <>
                            <img
                                src={BadgePDF.src}
                                width={BadgePDF.width}
                                height={BadgePDF.height}
                                alt="pdf badge"
                                className="pdf"
                            />
                            <img
                                src={BadgeDownload.src}
                                width={BadgeDownload.width}
                                height={BadgeDownload.height}
                                className="download"
                                alt="download badge"
                            />
                            <img
                                src={BadgePrint.src}
                                width={BadgePrint.width}
                                height={BadgePrint.height}
                                alt="print badge"
                                className="print"
                            />
                            {imageSource.map((image, i) =>
                                image ? (
                                    <KnowThumbnail pageNumber={++i as 1 | 2 | 3} key={`thumnail-${i}`}>
                                        <img src={image} width={250} height={360} alt={``} />
                                    </KnowThumbnail>
                                ) : null
                            )}
                        </>
                    ) : (
                        <div className="benefits">
                            <Body1>
                                {t('plusPlan/packageBenefitText1', {
                                    knowThousandsCount: KnowunityNumbers.knowThousandsCount,
                                })}
                            </Body1>
                            <Body1>{t('plusPlan/packageBenefitText2')}</Body1>
                            <Body1>{t('plusPlan/packageBenefitText3')}</Body1>
                            <Body1>{t('plusPlan/packageFeatureText4')}</Body1>
                        </div>
                    )}
                    {
                        <img
                            src={heroSource.src}
                            alt="Study notes screenshots"
                            width={heroSource.width}
                            height={heroSource.height}
                        />
                    }
                </div>
            ) : null}
        </Container>
    )
}

export default VariantHeader
